/**
 * <EmployeeOnboardingArchive /> — read-only archive of a completed
 * employee's onboarding, with timestamps, completed_by, and evidence links.
 *
 * Route: /admin/employees/:employeeId/onboarding-archive
 *
 * "Export as ZIP" is stubbed for Phase 2 per spec.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { ExternalLink, FileSignature, FileText, Lock } from 'lucide-react';
import { db } from '../../../firebase';
import {
  getSignature,
  getUploadedDocument,
} from '../services/onboardingService';
import '../Onboarding.css';

function fmtTs(v) {
  if (!v) return '—';
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return isNaN(d) ? '—' : d.toLocaleString();
}

export default function EmployeeOnboardingArchive() {
  const { employeeId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [items, setItems] = useState([]);
  const [evidence, setEvidence] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    let active = true;
    (async () => {
      const empSnap = await getDoc(doc(db, 'employees', employeeId));
      const itemsSnap = await getDocs(query(
        collection(db, 'checklist_items'),
        where('employee_id', '==', employeeId),
      ));
      const rows = itemsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (!active) return;
      setEmployee(empSnap.exists() ? { id: empSnap.id, ...empSnap.data() } : null);
      setItems(rows);

      const ev = {};
      for (const item of rows) {
        if (item.metadata?.signature_id) {
          const sig = await getSignature(item.metadata.signature_id);
          if (sig) ev[item.id] = { kind: 'signature', ...sig };
        } else if (item.metadata?.upload_id) {
          const up = await getUploadedDocument(item.metadata.upload_id);
          if (up) ev[item.id] = { kind: 'upload', ...up };
        }
      }
      if (active) {
        setEvidence(ev);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]);

  if (loading || !employee) {
    return <div style={{ padding: 16 }}><div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div></div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      <Link to="/admin/onboarding" className="onboarding-btn secondary" style={{ padding: '6px 10px', fontSize: '0.8rem' }}>
        ← Back
      </Link>
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
        Onboarding archive — {employee.firstName} {employee.lastName}
      </h1>
      <p style={{ color: '#555' }}>
        Hire date: {employee.hire_date || employee.hireDate || '—'} ·
        Completed: {fmtTs(employee.onboarding_completed_at)}
      </p>

      <button className="onboarding-btn secondary" disabled title="Phase 2">
        Export as ZIP (coming soon)
      </button>

      <table className="onboarding-table" style={{ marginTop: 16 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Item</th>
            <th>Type</th>
            <th>Completed</th>
            <th>By</th>
            <th>Evidence</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const ev = evidence[item.id];
            const evidenceLink = ev?.kind === 'signature'
              ? ev.document_snapshot_url
              : ev?.kind === 'upload' ? ev.file_url : null;
            return (
              <tr key={item.id}>
                <td>{idx + 1}</td>
                <td>{item.title}</td>
                <td>
                  {item.type === 'esign' && <><FileSignature size={12} /> e-sign</>}
                  {item.type === 'upload' && <><FileText size={12} /> upload</>}
                  {item.type === 'manager_verify' && <><Lock size={12} /> manager</>}
                </td>
                <td>{fmtTs(item.completed_at)}</td>
                <td>{item.completed_by || '—'}</td>
                <td>
                  {evidenceLink ? (
                    <a href={evidenceLink} target="_blank" rel="noreferrer">
                      <ExternalLink size={12} /> View
                    </a>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
